import React from 'react';
import Button from '../../../../../atoms/Button/Button';
import ListSimpleDropdown from '../../../../../molecules/ListSimpleDropdown/ListSimpleDropdown';
import Popover from '../../../../../molecules/Popover/Popover';
import useAddModuleMenuButton from './useAddModuleMenuButton';

/**
 * AddModuleMenuButton - Trigger "+ Añadir módulo" con menú de dos opciones
 * ("Añadir nuevo" / "Copiar módulo de otro programa").
 *
 * Reemplaza al `AddItemButton` del slot de módulo en todos los cursos
 * (el botón de submódulo sigue con el botón directo).
 *
 * Accesibilidad (patrón WAI-ARIA APG "menu button"): el trigger expone
 * `aria-haspopup="menu"` y `aria-expanded`; al abrir, el foco va al primer
 * `menuitem` (flechas, Home/End y type-ahead operativos), Escape y la
 * activación de una opción devuelven el foco al trigger, y `Tab` cierra el menú
 * sin atrapar el foco.
 */

type AddModuleMenuButtonProps = {
  /** Nombre accesible del trigger: hay uno por periodo y el texto visible se repite. */
  ariaLabel: string;
  label: string;
  onAddNew: () => void;
  onCopyModule: () => void;
};

function AddModuleMenuButton({ ariaLabel, label, onAddNew, onCopyModule }: AddModuleMenuButtonProps): JSX.Element {
  const { anchorEl, dismiss, getTriggerProps, isOpen, items, onOptionClick, panelContainer } = useAddModuleMenuButton({ onAddNew, onCopyModule });

  return (
    <>
      <Button
        {...getTriggerProps()}
        ariaLabel={ariaLabel}
        className="add-item-button add-item-button--module"
        isFullWidth
        text={`+ ${label}`}
        variant="primary-outline"
      />
      <Popover
        anchorEl={anchorEl}
        className="add-module-menu-button__panel"
        container={panelContainer}
        handleClose={dismiss}
        open={isOpen}
        placement="bottom-end"
        shouldCloseOnTabOut
        shouldFocusOnOpen={false}
      >
        <ListSimpleDropdown ariaLabel={label} items={items} onClick={onOptionClick} shouldAutoFocusFirstItem />
      </Popover>
    </>
  );
}

export default AddModuleMenuButton;
